import React, {
  Suspense,
  useMemo,
  useRef,
  useCallback,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import {
  CameraControls,
  Environment,
  Grid,
  useGLTF,
  Html,
  GizmoHelper,
  GizmoViewport,
  Outlines,
  Bvh,
  useCursor,
  PivotControls,
  useTexture,
  Line,
} from '@react-three/drei';
import * as THREE from 'three';
import { R1AS_ROOM_LABELS } from '../../data/r1asRoomLabels';
import {
  DEFAULT_SITE_PLANNER_MODEL,
  catalogGlbUrl,
  catalogEntry,
  catalogLabel,
} from '../../data/sitePlannerCatalog';
import { preloadCatalogModel } from '../../services/catalogModelPreload';
import GooglePhotorealisticTiles from './GooglePhotorealisticTiles';
import {
  LOT_PAD_THICKNESS,
  LOT_PAD_HALF,
  FIXED_LOAD_EYE,
  FLOOR_LOCK_STABLE_DELTA,
  FLOOR_LOCK_STABLE_COUNT,
  FLOOR_LOCK_TIMEOUT_SEC,
  canForceFloorLock,
  sampleLotFloorY,
  getFloorDebug,
  isFloorReadyForLock,
  bestEffortFloorY,
  applyFixedLoadCamera,
  lotCentroidXZ,
} from '../../services/photorealisticFloor';
import {
  TILE_ATTEMPT_TIMEOUT_SEC,
  getTileSnapshotBudget,
} from '../../services/photorealisticLoadLadder';
import {
  insetPlan,
  LOT_SCALE_MAX,
  LOT_SCALE_MIN,
  toLotVertices,
  toPlanVertices,
} from '../../utils/plotGeom';
import './SitePlannerViewer.css';

const INCH_TO_M = 1 / 39.37;

/** Camera eyes for a ~lot-scale view (meters from origin) */
const VIEW_PRESETS_NEAR = {
  iso: [18, 14, 22],
  front: [0, 8, 24],
  side: [24, 8, 0],
  top: [0, 40, 0.05],
};

/** Frame Photorealistic map so coverage ticker (sq mi) ≈ neighborhood in view. */
function viewPresetsForGround(groundMeters) {
  if (!groundMeters || groundMeters < 80) return VIEW_PRESETS_NEAR;
  // ~half the coverage side — zoom out with the ticker (no artificial low cap)
  const s = groundMeters * 0.5;
  return {
    iso: [s * 0.75, s * 0.5, s * 0.9],
    front: [0, s * 0.35, s * 1.05],
    side: [s * 1.05, s * 0.35, 0],
    top: [0, s * 1.2, 0.05],
  };
}

/**
 * Site planner R3F canvas — docs/ADU_SITE_PLANNER.md
 * Photorealistic budget ladder — docs/PHOTOREALISTIC_MOBILE_FALLBACK.md
 */
function SitePlannerViewer({
  site,
  lot,
  onLotChange,
  mapBuildings = [],
  contextMode = 'google',
  /** Photorealistic snapshot level: 0=full, 1=half, 2=quarter. */
  tileSnapshotLevel = 0,
  /** Escalate ladder when this Photorealistic attempt fails. */
  onTilesAttemptFailed,
  viewLocked = false,
  onViewLockedChange,
  lotSelected = false,
  onLotSelectedChange,
  /** Locked Photorealistic floor Y (null = still waiting / re-locking). */
  floorY = null,
  onFloorLock,
  onFloorDebug,
  floorDebug = null,
  structures,
  selectedId,
  onSelectStructure,
  onUpdateStructure,
  layers,
  section,
  onTileCredits,
  /** Notify parent which structure ids are still resolving GLBs. */
  onStructureLoadChange,
  /** Optional: parent binds Localize (Roomle chrome left rail). */
  onBindLocalize,
  /** Optional: parent binds camera preset keys iso|front|side|top. */
  onBindGoToView,
  /** Optional Locate overlays — setback inset ring (m) + north heading (deg). */
  setbackMeters = 0,
  northDeg = 0,
  /** Optional: highlight / report selected lot vertex (lot.vertices index). */
  selectedVertexIndex = null,
  onSelectVertex,
  className = '',
  heightClass = 'h-[min(80vh,720px)]',
}) {
  const controlsRef = useRef(null);
  const floorYRef = useRef(floorY);
  floorYRef.current = floorY;
  const lotVertsRef = useRef(lot?.vertices);
  lotVertsRef.current = lot?.vertices;
  const framedAfterLockRef = useRef(false);
  const tilesFailSentRef = useRef(false);
  // Prefer ticker-driven viewMeters so Photorealistic zoom matches the coverage slider
  const groundMeters = site?.viewMeters || site?.groundMeters;
  const [tileCredits, setTileCredits] = useState('');
  const tileBudget = useMemo(
    () => getTileSnapshotBudget(tileSnapshotLevel),
    [tileSnapshotLevel]
  );
  const viewPresets = useMemo(
    () => viewPresetsForGround(groundMeters),
    [groundMeters]
  );

  useEffect(() => {
    onTileCredits?.(tileCredits);
  }, [tileCredits, onTileCredits]);

  const showGoogleTiles = contextMode === 'google' && Boolean(site?.lat);
  const floorLocked = !showGoogleTiles || (floorY != null && Number.isFinite(floorY));
  const contentFloorY = floorLocked ? (showGoogleTiles ? Number(floorY) || 0 : 0) : 0;
  const defaultViewKey = 'iso';

  const reportTilesAttemptFailed = useCallback(
    (payload) => {
      if (!showGoogleTiles || floorLocked || tilesFailSentRef.current) return;
      tilesFailSentRef.current = true;
      onTilesAttemptFailed?.(payload);
    },
    [showGoogleTiles, floorLocked, onTilesAttemptFailed]
  );

  // New Photorealistic attempt (site / snapshot level) may fail again.
  useEffect(() => {
    tilesFailSentRef.current = false;
  }, [site?.lat, site?.lng, tileSnapshotLevel, showGoogleTiles]);

  // Per-attempt watchdog — escalate before the soft 45s floor lock spins forever.
  useEffect(() => {
    if (!showGoogleTiles || floorLocked) return undefined;
    const started = Date.now();
    const id = window.setInterval(() => {
      if (tilesFailSentRef.current) return;
      const elapsed = (Date.now() - started) / 1000;
      if (elapsed < TILE_ATTEMPT_TIMEOUT_SEC) return;
      const debug = getFloorDebug();
      // Prefer a best-effort lock when anything pickable exists; else degrade.
      if (canForceFloorLock(debug)) {
        const y = bestEffortFloorY(debug);
        if (y != null && Number.isFinite(y)) {
          onFloorLock?.(y);
          return;
        }
      }
      reportTilesAttemptFailed({
        reason: 'attempt-timeout',
        snapshotLevel: tileSnapshotLevel,
        budgetId: tileBudget.id,
      });
    }, 500);
    return () => window.clearInterval(id);
  }, [
    showGoogleTiles,
    floorLocked,
    tileSnapshotLevel,
    tileBudget.id,
    onFloorLock,
    reportTilesAttemptFailed,
  ]);

  const onCanvasCreated = useCallback(
    ({ gl }) => {
      const canvas = gl?.domElement;
      if (!canvas || !showGoogleTiles) return;
      const onLost = (e) => {
        e.preventDefault();
        reportTilesAttemptFailed({
          reason: 'webgl-context-lost',
          snapshotLevel: tileSnapshotLevel,
          budgetId: tileBudget.id,
        });
      };
      canvas.addEventListener('webglcontextlost', onLost, false);
    },
    [
      showGoogleTiles,
      tileSnapshotLevel,
      tileBudget.id,
      reportTilesAttemptFailed,
    ]
  );

  const goToView = useCallback(
    (key) => {
      if (viewLocked) return;
      const controls = controlsRef.current;
      if (!controls) return;
      const y = floorYRef.current ?? 0;
      const [cx, cz] = lotCentroidXZ(lotVertsRef.current);
      if (key === 'iso' || !viewPresets[key]) {
        applyFixedLoadCamera(controls, y, cx, cz);
        return;
      }
      const eye = viewPresets[key] || viewPresets.iso;
      controls.setLookAt(
        eye[0] + cx,
        eye[1] + y,
        eye[2] + cz,
        cx,
        y,
        cz,
        true,
      );
    },
    [viewPresets, viewLocked]
  );

  /** Fixed iso framing on the lot centroid — lock, Reset, coverage. */
  const frameSiteFixed = useCallback(() => {
    if (viewLocked) return;
    const controls = controlsRef.current;
    if (!controls) return;
    const [cx, cz] = lotCentroidXZ(lotVertsRef.current);
    applyFixedLoadCamera(controls, floorYRef.current ?? 0, cx, cz);
  }, [viewLocked]);

  const localizeAndLock = useCallback(() => {
    const controls = controlsRef.current;
    if (!controls || !lot?.vertices?.length) return;
    const verts = lot.vertices;
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    let sx = 0;
    let sz = 0;
    for (const [x, z] of verts) {
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minZ = Math.min(minZ, z);
      maxZ = Math.max(maxZ, z);
      sx += x;
      sz += z;
    }
    const cx = sx / verts.length;
    const cz = sz / verts.length;
    const span = Math.max(maxX - minX, maxZ - minZ, 16);
    const height = Math.max(span * 2.8, 35);
    const groundY = floorYRef.current ?? 0;
    controls.enabled = true;
    controls.setLookAt(
      cx,
      groundY + height,
      cz + 0.05,
      cx,
      groundY,
      cz,
      true
    );
    onViewLockedChange?.(true);
    onLotSelectedChange?.(true);
  }, [lot?.vertices, onViewLockedChange, onLotSelectedChange]);

  useEffect(() => {
    onBindLocalize?.(localizeAndLock);
    return () => onBindLocalize?.(null);
  }, [onBindLocalize, localizeAndLock]);

  useEffect(() => {
    onBindGoToView?.(goToView);
    return () => onBindGoToView?.(null);
  }, [onBindGoToView, goToView]);

  // Frame once when floor first locks — not on every floorY tweak / model load.
  useEffect(() => {
    if (!floorLocked) {
      framedAfterLockRef.current = false;
      return;
    }
    if (framedAfterLockRef.current) return;
    framedAfterLockRef.current = true;
    frameSiteFixed();
  }, [floorLocked, frameSiteFixed]);

  // Coverage ticker change: reframe lot (same iso offset).
  const groundMetersPrev = useRef(groundMeters);
  useEffect(() => {
    if (groundMetersPrev.current === groundMeters) return;
    groundMetersPrev.current = groundMeters;
    if (floorLocked) frameSiteFixed();
  }, [groundMeters, floorLocked, frameSiteFixed]);

  useEffect(() => {
    const controls = controlsRef.current;
    if (controls) controls.enabled = !viewLocked;
  }, [viewLocked]);

  const toolBtn =
    'px-2.5 py-1 text-xs md:text-sm rounded-[10px] border border-black/10 bg-white/90 hover:bg-white backdrop-blur-sm transition-colors font-bai';
  const toolBtnActive =
    'px-2.5 py-1 text-xs md:text-sm rounded-[10px] border border-gray-900 bg-gray-900 text-white backdrop-blur-sm transition-colors font-bai';

  const sectionPlane = useMemo(() => {
    if (!section?.enabled) return null;
    return new THREE.Plane(new THREE.Vector3(1, 0, 0), -section.offset);
  }, [section?.enabled, section?.offset]);

  const camNear = 1;
  const camFar = Math.max(5000, (groundMeters || 720) * 5);
  const maxDist = Math.max(2500, (groundMeters || 720) * 3.5);
  const showFlatSat = Boolean(site?.objectUrl) && !showGoogleTiles;
  const showOsm = contextMode === 'osm' && mapBuildings.length > 0;

  const fullBleed = heightClass === 'h-full';

  return (
    <div
      className={`relative w-full ${heightClass} overflow-hidden bg-[#d9d4cc] ${
        fullBleed ? 'rounded-none' : 'rounded-[10px]'
      } ${className}`}
    >
      {/* Non full-bleed only — Roomle chrome owns view controls via the left rail. */}
      {!fullBleed && (
        <div className="absolute z-10 top-3 left-3 flex flex-wrap gap-1.5">
          {['iso', 'front', 'side', 'top'].map((k) => (
            <button
              key={k}
              type="button"
              className={toolBtn}
              disabled={viewLocked}
              onClick={() => goToView(k)}
            >
              {k[0].toUpperCase() + k.slice(1)}
            </button>
          ))}
          <button
            type="button"
            className={toolBtn}
            disabled={!lot?.vertices?.length}
            onClick={localizeAndLock}
            title="Top-down on lot and lock camera"
          >
            Localize
          </button>
          <button
            type="button"
            className={viewLocked ? toolBtnActive : toolBtn}
            disabled={!site}
            onClick={() => onViewLockedChange?.(!viewLocked)}
          >
            {viewLocked ? 'Unlock' : 'Lock'}
          </button>
        </div>
      )}

      {viewLocked ? (
        <div
          className={`absolute z-10 pointer-events-none ${
            fullBleed ? 'top-16 left-3 md:left-4' : 'top-3 right-3'
          }`}
        >
          <span className="text-[10px] uppercase tracking-wide bg-gray-900/80 text-white px-2 py-1 rounded-full font-bai">
            View locked
          </span>
        </div>
      ) : null}

      {showGoogleTiles && floorLocked && tileCredits ? (
        <div
          className="site-planner-attr"
          title={tileCredits}
          aria-label="Map imagery attribution"
        >
          <p className="site-planner-attr__text">{tileCredits}</p>
        </div>
      ) : null}

      <Canvas
        key={
          showGoogleTiles
            ? `gl-tiles-${site?.lat}-${site?.lng}-${tileBudget.id}`
            : 'gl-flat'
        }
        shadows={false}
        dpr={[1, tileBudget.dprMax]}
        frameloop="always"
        camera={{
          // Photorealistic load must start near the pin — coverage-scale eyes
          // starve tile LODs and leave “Waiting for tiles…” forever.
          position: showGoogleTiles
            ? FIXED_LOAD_EYE
            : viewPresets[defaultViewKey] || viewPresets.iso,
          fov: 40,
          near: camNear,
          far: camFar,
        }}
        gl={{
          antialias: !showGoogleTiles || tileBudget.id === 'full',
          powerPreference: 'default',
          toneMapping: THREE.ACESFilmicToneMapping,
          localClippingEnabled: true,
          logarithmicDepthBuffer: !showGoogleTiles,
        }}
        onCreated={onCanvasCreated}
        onPointerMissed={() => {
          onSelectStructure?.(null);
          onLotSelectedChange?.(false);
        }}
      >
        <CameraFrustumSync near={camNear} far={camFar} />
        <ClippingSetup enabled={Boolean(section?.enabled)} />
        <color attach="background" args={['#c8c2b8']} />
        <ambientLight intensity={0.7} />
        <directionalLight
          intensity={0.85}
          position={[
            (groundMeters || 40) * 0.4,
            (groundMeters || 40) * 0.6,
            (groundMeters || 40) * 0.25,
          ]}
        />

        {/* Tiles + floor lock stay outside Suspense so GLB/HDR loads never dispose them. */}
        {showGoogleTiles && (
          <GooglePhotorealisticTiles
            lat={site.lat}
            lng={site.lng}
            height={Number(site.elevationMeters) || 0}
            enabled
            snapshotLevel={tileSnapshotLevel}
            onCredits={setTileCredits}
            onReady={frameSiteFixed}
            onAttemptFailed={reportTilesAttemptFailed}
          />
        )}

        {showGoogleTiles && (
          <FloorLockController
            enabled
            locked={floorLocked}
            lotVertices={lot?.vertices}
            controlsRef={controlsRef}
            onFloorLock={onFloorLock}
            onFloorDebug={onFloorDebug}
          />
        )}

        <Suspense fallback={<LoaderNote />}>
          {showFlatSat ? (
            <SatelliteGround
              url={site.objectUrl}
              groundMeters={site.groundMeters}
            />
          ) : !site?.objectUrl && !showGoogleTiles ? (
            <DefaultGround />
          ) : null}

          {showOsm && <MapBuildings buildings={mapBuildings} />}

          {/* Lot + ADUs only after floor is locked (or non-Google context). */}
          {floorLocked && (
            <group
              position={[0, contentFloorY, 0]}
              rotation={[0, (Number(northDeg) || 0) * (Math.PI / 180), 0]}
            >
              <LotBoundary
                vertices={lot.vertices}
                elevation={Number(lot.y) || 0}
                selected={lotSelected}
                onSelect={() => {
                  onLotSelectedChange?.(true);
                  onSelectStructure?.(null);
                }}
                onLotChange={onLotChange}
                controlsRef={controlsRef}
                viewLocked={viewLocked}
                selectedVertexIndex={selectedVertexIndex}
                onSelectVertex={onSelectVertex}
                northDeg={northDeg}
              />
              {Number(setbackMeters) > 0 && lot?.vertices?.length >= 3 && (
                <SetbackInset
                  vertices={lot.vertices}
                  setbackMeters={Number(setbackMeters)}
                  elevation={Number(lot.y) || 0}
                />
              )}

              <Bvh firstHitOnly>
                {structures.map((s) => (
                  <AduLoadBoundary
                    key={s.id}
                    structure={s}
                    onStructureLoadChange={onStructureLoadChange}
                  >
                    <Suspense
                      fallback={
                        <AduLoadingPlaceholder
                          structure={s}
                          onStructureLoadChange={onStructureLoadChange}
                        />
                      }
                    >
                      <AduInstance
                        structure={s}
                        selected={selectedId === s.id}
                        layers={layers}
                        sectionPlane={sectionPlane}
                        onSelect={() => {
                          onSelectStructure?.(s.id);
                          onLotSelectedChange?.(false);
                        }}
                        onMove={(position, rotationY) =>
                          onUpdateStructure?.(s.id, { position, rotationY })
                        }
                        onStructureLoadChange={onStructureLoadChange}
                      />
                    </Suspense>
                  </AduLoadBoundary>
                ))}
              </Bvh>
            </group>
          )}

          {!showGoogleTiles && <Environment preset="warehouse" />}
        </Suspense>

        {!site?.objectUrl && !showGoogleTiles && (
          <Grid
            position={[0, -0.001, 0]}
            args={[60, 60]}
            cellSize={0.5}
            cellThickness={0.5}
            cellColor="#b5aea3"
            sectionSize={5}
            sectionThickness={1}
            sectionColor="#8a8278"
            fadeDistance={50}
            infiniteGrid
          />
        )}

        <CameraControls
          ref={controlsRef}
          makeDefault
          enabled={!viewLocked}
          minDistance={2}
          maxDistance={maxDist}
          minPolarAngle={0.05}
          maxPolarAngle={Math.PI * 0.49}
          smoothTime={0.35}
        />
        {floorLocked && (
          <GizmoHelper alignment="bottom-right" margin={[72, 72]}>
            <GizmoViewport
              axisColors={['#c45c3e', '#5a8f5a', '#4a6fa5']}
              labelColor="white"
            />
          </GizmoHelper>
        )}
      </Canvas>
    </div>
  );
}

function CameraFrustumSync({ near, far }) {
  const { camera } = useThree();
  useEffect(() => {
    camera.near = near;
    camera.far = far;
    camera.updateProjectionMatrix();
  }, [camera, near, far]);
  return null;
}

function ClippingSetup({ enabled }) {
  const { gl } = useThree();
  useEffect(() => {
    gl.localClippingEnabled = enabled;
  }, [gl, enabled]);
  return null;
}

/** Keep below xlabs compact Models sheet (z ≈ 2^31). See docs/XLABS_DESIGN.md */
const HTML_Z = [100, 0];

function LoaderNote() {
  return (
    <Html center zIndexRange={HTML_Z}>
      <p className="font-bai text-sm text-gray-700 bg-white/80 px-3 py-2 rounded-[10px]">
        Loading…
      </p>
    </Html>
  );
}

/**
 * Catch GLB load failures (e.g. prod SPA HTML for missing /models/*.glb)
 * so Photorealistic tiles / WebGL stay alive. Docs: docs/MODEL_ASSETS.md
 */
class AduLoadBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    const { structure, onStructureLoadChange } = this.props;
    onStructureLoadChange?.(structure.id, false, structure.type);
    // eslint-disable-next-line no-console
    console.error('[AduLoadBoundary]', structure?.type, error?.message || error);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.structure?.id !== this.props.structure?.id ||
      prevProps.structure?.type !== this.props.structure?.type
    ) {
      if (this.state.error) this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <AduLoadErrorPlaceholder
          structure={this.props.structure}
          message={this.state.error?.message || 'Model failed to load'}
        />
      );
    }
    return this.props.children;
  }
}

function AduLoadErrorPlaceholder({ structure, message }) {
  const [x, y = 0, z] = structure.position || [0, 0, 0];
  const short =
    /doctype|not valid JSON|Failed to fetch|404/i.test(message || '')
      ? 'GLB missing in this environment'
      : 'Model failed to load';
  return (
    <group position={[x, y, z]} rotation-y={structure.rotationY || 0}>
      <mesh position={[0, 1.2, 0]}>
        <boxGeometry args={[3.2, 2.4, 3.2]} />
        <meshStandardMaterial
          color="#8a2e24"
          transparent
          opacity={0.35}
          wireframe
        />
      </mesh>
      <Html center position={[0, 2.8, 0]} style={{ pointerEvents: 'none' }}>
        <div className="font-bai text-[11px] px-2 py-1 rounded-[10px] bg-red-950/85 text-red-100 max-w-[200px] text-center leading-snug">
          {catalogLabel(structure.type)} · {short}
        </div>
      </Html>
    </group>
  );
}

/** Per-instance Suspense fallback — small fast spinner at the place pose. */
function AduLoadingPlaceholder({ structure, onStructureLoadChange }) {
  const [x, y = 0, z] = structure.position || [0, 0, 0];
  const id = structure.id;
  const type = structure.type;

  useEffect(() => {
    onStructureLoadChange?.(id, true, type);
  }, [id, type, onStructureLoadChange]);

  return (
    <group position={[x, y, z]}>
      <Html
        center
        position={[0, 1.4, 0]}
        style={{ pointerEvents: 'none' }}
        zIndexRange={HTML_Z}
        distanceFactor={12}
      >
        <div
          className="adu-lot-spinner"
          aria-label={`Loading ${catalogLabel(type)}`}
        >
          <span className="adu-lot-spinner__ring" />
        </div>
      </Html>
    </group>
  );
}

function DefaultGround() {
  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -0.02, 0]}
      receiveShadow
    >
      <planeGeometry args={[60, 60]} />
      <meshStandardMaterial color="#cdc6bc" roughness={0.95} />
    </mesh>
  );
}

function SatelliteGround({ url, groundMeters }) {
  const texture = useTexture(url);
  useEffect(() => {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8;
    texture.generateMipmaps = true;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;
  }, [texture]);

  // Unlit map avoids shadow acne / depth flicker on huge planes
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} renderOrder={-1}>
      <planeGeometry args={[groundMeters, groundMeters]} />
      <meshBasicMaterial map={texture} toneMapped={false} depthWrite />
    </mesh>
  );
}

const FALLBACK_PLANE = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

/** Skip lot UI + Line2 overlays (PivotControls/Outlines crash without raycaster.camera). */
function isGroundPickable(obj) {
  if (!obj?.visible || obj.userData?.lotUi) return false;
  if (
    obj.isLine ||
    obj.isLineSegments ||
    obj.isLine2 ||
    obj.isLineSegments2 ||
    obj.type === 'Line' ||
    obj.type === 'Line2' ||
    obj.type === 'LineSegments2'
  ) {
    return false;
  }
  return Boolean(obj.isMesh || obj.isInstancedMesh || obj.isBatchedMesh);
}

function collectGroundTargets(root) {
  const out = [];
  root.traverse((obj) => {
    if (isGroundPickable(obj)) out.push(obj);
  });
  return out;
}

function pickGroundHit(raycaster, scene, camera, ndc, target) {
  raycaster.setFromCamera(ndc, camera);
  raycaster.camera = camera;
  const hits = raycaster.intersectObjects(collectGroundTargets(scene), false);
  if (hits.length) {
    target.copy(hits[0].point);
    return target;
  }
  if (raycaster.ray.intersectPlane(FALLBACK_PLANE, target)) return target;
  return null;
}

/** World XZ → lot-local XZ when lot group has rotation.y = northDeg (deg). */
function worldXzToLotLocal(wx, wz, northDeg = 0) {
  const θ = (Number(northDeg) || 0) * (Math.PI / 180);
  if (!θ) return [wx, wz];
  const c = Math.cos(θ);
  const s = Math.sin(θ);
  return [wx * c - wz * s, wx * s + wz * c];
}

/**
 * Poll tile LODs until a plausible floor exists, then lock once.
 * Samples / frames the lot footprint (not only the geocode pin).
 * After lock: no automatic floorY step-down (prevents lot fall-through).
 */
function FloorLockController({
  enabled,
  locked,
  lotVertices,
  controlsRef,
  onFloorLock,
  onFloorDebug,
}) {
  const { scene, camera, invalidate } = useThree();
  const debugAcc = useRef(0);
  const waitAcc = useRef(0);
  const camNudgeAcc = useRef(0);
  const lockSent = useRef(false);
  const stableYs = useRef([]);
  const sceneRef = useRef(scene);
  const cameraRef = useRef(camera);
  const lotVertsRef = useRef(lotVertices);
  sceneRef.current = scene;
  cameraRef.current = camera;
  lotVertsRef.current = lotVertices;

  const sampleFloor = useCallback((scn, cam) => {
    const verts = lotVertsRef.current;
    if (verts?.length) return sampleLotFloorY(scn, verts, cam);
    // Boot fallback before lot verts exist
    return sampleLotFloorY(scn, null, cam);
  }, []);

  const frameLot = useCallback(
    (y = 0) => {
      if (!controlsRef?.current) return;
      const [cx, cz] = lotCentroidXZ(lotVertsRef.current);
      applyFixedLoadCamera(controlsRef.current, y, cx, cz);
    },
    [controlsRef],
  );

  useEffect(() => {
    if (!locked) {
      lockSent.current = false;
      stableYs.current = [];
      waitAcc.current = 0;
    }
  }, [locked]);

  // Wall-clock timeout — does not depend on the R3F frame loop (which can stall
  // before tiles/controls are ready and left the UI spinning forever).
  useEffect(() => {
    if (!enabled || locked) return undefined;
    const started = Date.now();
    const id = window.setInterval(() => {
      if (lockSent.current) return;
      const elapsed = (Date.now() - started) / 1000;
      waitAcc.current = elapsed;
      // Keep camera on the lot so LODs stream where the pad will sit.
      frameLot(0);
      invalidate();
      try {
        sampleFloor(sceneRef.current, cameraRef.current);
      } catch {
        /* ignore sample errors during boot */
      }
      const debug = getFloorDebug();
      onFloorDebug?.({
        ...debug,
        stableCount: stableYs.current.length,
        waitSec: Math.round(elapsed * 10) / 10,
      });
      if (elapsed < FLOOR_LOCK_TIMEOUT_SEC) return;
      if (!canForceFloorLock(debug)) return;
      const y = bestEffortFloorY(debug);
      if (y == null || !Number.isFinite(y)) return;
      lockSent.current = true;
      onFloorLock?.(y);
      frameLot(y);
    }, 500);
    return () => window.clearInterval(id);
  }, [
    enabled,
    locked,
    controlsRef,
    onFloorLock,
    onFloorDebug,
    invalidate,
    frameLot,
    sampleFloor,
  ]);

  useFrame((_, dt) => {
    if (!enabled) return;

    // While waiting: nudge camera toward the lot so Photorealistic LODs stream in.
    if (!locked && controlsRef?.current) {
      camNudgeAcc.current += dt;
      if (camNudgeAcc.current > 1.2) {
        camNudgeAcc.current = 0;
        frameLot(0);
      }
    }

    debugAcc.current += dt;
    const sampleEvery = locked ? 0.75 : 0.25;
    if (debugAcc.current < sampleEvery) return;
    debugAcc.current = 0;

    sampleFloor(scene, camera);
    const debug = getFloorDebug();
    onFloorDebug?.({
      ...debug,
      stableCount: stableYs.current.length,
      waitSec: Math.round(waitAcc.current * 10) / 10,
    });

    // Locked: keep sampling for debug HUD only — never step floorY or reframe.
    if (locked || lockSent.current) return;

    if (!isFloorReadyForLock(debug)) {
      stableYs.current = [];
      return;
    }

    const y = debug.sampleY;
    const prev = stableYs.current;
    if (
      prev.length &&
      Math.abs(prev[prev.length - 1] - y) > FLOOR_LOCK_STABLE_DELTA
    ) {
      stableYs.current = [y];
      return;
    }
    stableYs.current = [...prev, y].slice(-FLOOR_LOCK_STABLE_COUNT);
    if (stableYs.current.length < FLOOR_LOCK_STABLE_COUNT) return;

    const avg =
      stableYs.current.reduce((s, v) => s + v, 0) / stableYs.current.length;
    lockSent.current = true;
    onFloorLock?.(avg);
    frameLot(avg);
  });

  return null;
}

/** Setback inset ring for xlabs Locate hybrid — docs/XLABS_LOCATE.md */
function SetbackInset({ vertices, setbackMeters, elevation = 0 }) {
  const points = useMemo(() => {
    if (!vertices?.length || !(setbackMeters > 0)) return null;
    const plan = toPlanVertices(vertices);
    const inset = insetPlan(plan, setbackMeters);
    if (!inset?.length) return null;
    const xz = toLotVertices(inset);
    const y = elevation + LOT_PAD_THICKNESS + 0.04;
    const pts = xz.map(([x, z]) => [x, y, z]);
    pts.push(pts[0]);
    return pts;
  }, [vertices, setbackMeters, elevation]);

  if (!points) return null;
  return (
    <Line
      points={points}
      color="#2b4eff"
      lineWidth={1.5}
      transparent
      opacity={0.85}
      depthTest
    />
  );
}

function LotBoundary({
  vertices,
  elevation = 0,
  selected,
  onSelect,
  onLotChange,
  controlsRef,
  viewLocked,
  selectedVertexIndex = null,
  onSelectVertex,
  northDeg = 0,
}) {
  const { scene, camera, gl } = useThree();
  const groupRef = useRef();
  const floorLocalRef = useRef(LOT_PAD_HALF);
  const pivotDragging = useRef(false);
  const pending = useRef(null);
  const matrix = useRef(new THREE.Matrix4()).current;
  const [hovered, setHovered] = useState(false);
  useCursor(hovered || selected);

  const bodyDrag = useRef(null);
  const hit = useMemo(() => new THREE.Vector3(), []);
  const ndc = useMemo(() => new THREE.Vector2(), []);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);

  const n = vertices?.length || 1;
  const cx = (vertices || []).reduce((s, v) => s + v[0], 0) / n;
  const cz = (vertices || []).reduce((s, v) => s + v[1], 0) / n;
  const localVertices = useMemo(
    () => (vertices || []).map(([x, z]) => [x - cx, z - cz]),
    [vertices, cx, cz]
  );

  const padGeo = useMemo(() => {
    if (!localVertices.length) return null;
    const shape = new THREE.Shape();
    shape.moveTo(localVertices[0][0], -localVertices[0][1]);
    for (let i = 1; i < localVertices.length; i++) {
      shape.lineTo(localVertices[i][0], -localVertices[i][1]);
    }
    shape.closePath();
    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: LOT_PAD_THICKNESS,
      bevelEnabled: false,
    });
    // Extrude along +Z → rotate so thickness is +Y (pad sits on floor).
    geo.rotateX(-Math.PI / 2);
    return geo;
  }, [localVertices]);

  const edgePoints = useMemo(() => {
    if (!localVertices.length) return [];
    const y = LOT_PAD_THICKNESS + 0.02;
    const closed = [...localVertices, localVertices[0]];
    return closed.map(([x, z]) => [x, y, z]);
  }, [localVertices]);

  const commitLot = useCallback(
    (nextVertices, nextY = elevation) => {
      onLotChange?.({
        vertices: nextVertices,
        y: Number(nextY) || 0,
      });
    },
    [onLotChange, elevation]
  );

  const moveVertex = useCallback(
    (index, worldXz) => {
      const local = worldXzToLotLocal(worldXz[0], worldXz[1], northDeg);
      const next = vertices.map((v, i) => (i === index ? local : [...v]));
      commitLot(next, elevation);
    },
    [vertices, commitLot, elevation, northDeg]
  );

  useEffect(() => {
    const onMoveWin = (e) => {
      if (!bodyDrag.current) return;
      e.preventDefault();
      const rect = gl.domElement.getBoundingClientRect();
      ndc.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      const p = pickGroundHit(raycaster, scene, camera, ndc, hit);
      if (!p) return;
      const { origin, startVertices } = bodyDrag.current;
      const local = worldXzToLotLocal(p.x, p.z, northDeg);
      const dx = local[0] - origin[0];
      const dz = local[1] - origin[1];
      commitLot(
        startVertices.map(([x, z]) => [x + dx, z + dz]),
        elevation
      );
    };
    const onUp = () => {
      if (!bodyDrag.current) return;
      bodyDrag.current = null;
      if (controlsRef?.current && !viewLocked) {
        controlsRef.current.enabled = true;
      }
    };
    window.addEventListener('pointermove', onMoveWin, { passive: false });
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMoveWin);
      window.removeEventListener('pointerup', onUp);
    };
  }, [
    camera,
    gl,
    scene,
    hit,
    ndc,
    raycaster,
    commitLot,
    elevation,
    controlsRef,
    viewLocked,
    northDeg,
  ]);

  // Pad geom sits on y=0…thickness; group/matrix Y is only the manual nudge.
  const syncPose = useCallback(() => {
    const y = Number(elevation) || 0;
    floorLocalRef.current = LOT_PAD_HALF;
    matrix.compose(
      new THREE.Vector3(cx, y, cz),
      new THREE.Quaternion(),
      new THREE.Vector3(1, 1, 1)
    );
    if (!groupRef.current) return;
    if (selected) {
      groupRef.current.position.set(0, 0, 0);
    } else {
      groupRef.current.position.set(cx, y, cz);
    }
  }, [cx, cz, elevation, matrix, selected]);

  useLayoutEffect(() => {
    syncPose();
  }, [syncPose]);

  useFrame(() => {
    if (!pivotDragging.current) syncPose();
  });

  const startBodyDrag = (e) => {
    e.stopPropagation();
    onSelect?.();
    const rect = gl.domElement.getBoundingClientRect();
    ndc.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    );
    const p = pickGroundHit(raycaster, scene, camera, ndc, hit);
    if (!p) return;
    bodyDrag.current = {
      origin: worldXzToLotLocal(p.x, p.z, northDeg),
      startVertices: vertices.map((v) => [...v]),
    };
    if (controlsRef?.current) controlsRef.current.enabled = false;
  };

  const lotMesh = (
    <>
      {padGeo && (
        <mesh
          geometry={padGeo}
          userData={{ lotUi: true }}
          renderOrder={30}
          castShadow={selected}
          onPointerOver={(e) => {
            e.stopPropagation();
            setHovered(true);
          }}
          onPointerOut={() => setHovered(false)}
          onPointerDown={startBodyDrag}
        >
          <meshStandardMaterial
            color={selected ? '#f0c43a' : '#e8b923'}
            transparent
            // Deselected: ghost fill so site/ADUs read through; edges carry the lot.
            opacity={selected ? 0.88 : hovered ? 0.28 : 0.08}
            roughness={0.85}
            metalness={0.05}
            side={THREE.DoubleSide}
            depthWrite={selected}
          />
        </mesh>
      )}
      {edgePoints.length > 1 && (
        <>
          {/* Perimeter stays readable when fill is ghosted (lot–ground intersection). */}
          <Line
            points={edgePoints}
            color={selected ? '#2a1f05' : '#c9a227'}
            lineWidth={selected ? 5 : 4}
            depthTest={false}
            renderOrder={32}
            userData={{ lotUi: true }}
          />
          <Line
            points={edgePoints.map(([x, y, z]) => [x, y - LOT_PAD_THICKNESS, z])}
            color={selected ? '#5c4508' : '#a8861c'}
            lineWidth={selected ? 3 : 2.5}
            depthTest={false}
            renderOrder={31}
            userData={{ lotUi: true }}
          />
        </>
      )}
      {!selected && (
        <Html
          position={[0, LOT_PAD_THICKNESS + 1.1, 0]}
          center
          style={{ pointerEvents: 'none' }}
          zIndexRange={[10, 0]}
        >
          <div className="font-bai text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded text-white/90 bg-amber-900/55">
            Lot
          </div>
        </Html>
      )}
      {selected &&
        localVertices.map((v, i) => (
          <LotVertexHandle
            key={i}
            index={i}
            localPosition={v}
            onMove={moveVertex}
            controlsRef={controlsRef}
            viewLocked={viewLocked}
            active={selectedVertexIndex === i}
            onSelect={() => {
              onSelect?.();
              onSelectVertex?.(i);
            }}
          />
        ))}
    </>
  );

  if (!selected) {
    return <group ref={groupRef}>{lotMesh}</group>;
  }

  return (
    <group ref={groupRef}>
      <PivotControls
        matrix={matrix}
        autoTransform={false}
        anchor={[0, 0, 0]}
        scale={4}
        lineWidth={2}
        activeAxes={[true, true, true]}
        disableRotations
        annotations={false}
        depthTest={false}
        onDragStart={() => {
          pivotDragging.current = true;
          if (controlsRef?.current) controlsRef.current.enabled = false;
        }}
        onDrag={(local) => {
          const pos = new THREE.Vector3();
          const quat = new THREE.Quaternion();
          const scl = new THREE.Vector3();
          local.decompose(pos, quat, scl);
          // Uniform XZ scale only — keep pad thickness (Y) at 1.
          const s = Math.min(
            LOT_SCALE_MAX,
            Math.max(
              LOT_SCALE_MIN,
              (Math.abs(scl.x) + Math.abs(scl.z)) / 2 || 1
            )
          );
          matrix.compose(pos, quat, new THREE.Vector3(s, 1, s));
          pending.current = {
            vertices: localVertices.map(([lx, lz]) => [
              lx * s + pos.x,
              lz * s + pos.z,
            ]),
            y: pos.y,
          };
        }}
        onDragEnd={() => {
          pivotDragging.current = false;
          if (controlsRef?.current && !viewLocked) {
            controlsRef.current.enabled = true;
          }
          if (!pending.current) return;
          commitLot(pending.current.vertices, pending.current.y);
          pending.current = null;
          // Geometry rebuilds from baked vertices — reset gizmo scale.
          matrix.compose(
            new THREE.Vector3(cx, Number(elevation) || 0, cz),
            new THREE.Quaternion(),
            new THREE.Vector3(1, 1, 1)
          );
        }}
      >
        {lotMesh}
      </PivotControls>
    </group>
  );
}

function LotVertexHandle({
  index,
  localPosition,
  onMove,
  controlsRef,
  viewLocked,
  active = false,
  onSelect,
}) {
  const meshRef = useRef();
  const dragging = useRef(false);
  const [hovered, setHovered] = useState(false);
  useCursor(hovered || dragging.current || active);
  const { camera, gl, scene } = useThree();
  const hit = useMemo(() => new THREE.Vector3(), []);
  const ndc = useMemo(() => new THREE.Vector2(), []);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);

  useEffect(() => {
    const onMoveWin = (e) => {
      if (!dragging.current) return;
      e.preventDefault();
      const rect = gl.domElement.getBoundingClientRect();
      ndc.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      const p = pickGroundHit(raycaster, scene, camera, ndc, hit);
      if (p) onMove(index, [p.x, p.z]);
    };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      if (controlsRef?.current && !viewLocked) {
        controlsRef.current.enabled = true;
      }
    };
    window.addEventListener('pointermove', onMoveWin, { passive: false });
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMoveWin);
      window.removeEventListener('pointerup', onUp);
    };
  }, [
    camera,
    gl,
    scene,
    hit,
    index,
    ndc,
    onMove,
    raycaster,
    controlsRef,
    viewLocked,
  ]);

  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    mesh.position.set(localPosition[0], LOT_PAD_THICKNESS + 0.35, localPosition[1]);
    const d = camera.position.distanceTo(mesh.getWorldPosition(hit));
    const s = Math.min(2.8, Math.max(0.7, d * 0.012));
    mesh.scale.setScalar(s);
  });

  return (
    <mesh
      ref={meshRef}
      position={[localPosition[0], LOT_PAD_THICKNESS + 0.35, localPosition[1]]}
      renderOrder={20}
      userData={{ lotUi: true }}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
      }}
      onPointerOut={() => setHovered(false)}
      onPointerDown={(e) => {
        e.stopPropagation();
        onSelect?.();
        dragging.current = true;
        if (controlsRef?.current) controlsRef.current.enabled = false;
      }}
    >
      <sphereGeometry args={[0.55, 18, 18]} />
      <meshBasicMaterial
        color={active ? '#2b4eff' : hovered ? '#fff1a8' : '#e8b923'}
        depthTest={false}
      />
    </mesh>
  );
}

function MapBuildings({ buildings }) {
  const items = useMemo(() => {
    const out = [];
    for (const b of buildings) {
      try {
        out.push({ id: b.id, geometry: extrudeBuildingFootprint(b) });
      } catch {
        /* skip degenerate rings */
      }
    }
    return out;
  }, [buildings]);

  useEffect(
    () => () => {
      items.forEach(({ geometry }) => geometry.dispose());
    },
    [items]
  );

  const material = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: '#cfc8bc',
        roughness: 0.92,
        metalness: 0.02,
        transparent: true,
        opacity: 0.88,
        depthWrite: true,
      }),
    []
  );

  useEffect(() => () => material.dispose(), [material]);

  return (
    <group>
      {items.map(({ id, geometry }) => (
        <mesh
          key={id}
          geometry={geometry}
          material={material}
          raycast={() => null}
        />
      ))}
    </group>
  );
}

function extrudeBuildingFootprint({ points, height }) {
  const shape = new THREE.Shape();
  shape.moveTo(points[0][0], -points[0][1]);
  for (let i = 1; i < points.length; i++) {
    shape.lineTo(points[i][0], -points[i][1]);
  }
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: Math.max(2, height || 6),
    bevelEnabled: false,
  });
  geo.rotateX(-Math.PI / 2);
  return geo;
}

function AduInstance({
  structure,
  selected,
  layers,
  sectionPlane,
  onSelect,
  onMove,
  onStructureLoadChange,
}) {
  const slug =
    catalogEntry(structure.type)?.slug || DEFAULT_SITE_PLANNER_MODEL;
  const modelUrl = catalogGlbUrl(slug);
  const { scene: gltfScene } = useGLTF(modelUrl);
  const [hovered, setHovered] = useState(false);
  useCursor(hovered || selected);

  useEffect(() => {
    onStructureLoadChange?.(structure.id, false, structure.type);
  }, [onStructureLoadChange, structure.id, structure.type, modelUrl]);

  const meshes = useMemo(() => prepareLayeredMeshes(gltfScene), [gltfScene]);
  const roomLabels = slug === 'r1as' ? R1AS_ROOM_LABELS : [];
  const planes = sectionPlane ? [sectionPlane] : [];
  const [x, y = 0, z] = structure.position;
  const rotY = structure.rotationY || 0;

  // Controlled transform — do NOT use PivotControls `offset` for placement
  // (offset only moves the gizmo; children stay at origin and "disappear" on save).
  const matrix = useRef(new THREE.Matrix4()).current;
  useLayoutEffect(() => {
    const q = new THREE.Quaternion().setFromEuler(
      new THREE.Euler(0, rotY, 0, 'YXZ')
    );
    matrix.compose(
      new THREE.Vector3(x, y, z),
      q,
      new THREE.Vector3(1, 1, 1)
    );
  }, [matrix, x, y, z, rotY]);

  const pending = useRef(null);

  const body = (
    <group
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.();
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
      }}
      onPointerOut={() => setHovered(false)}
    >
      <group scale={INCH_TO_M}>
        {meshes.map((m) => {
          const layerKey = layerKeyFromName(m.name);
          if (layers && layers[layerKey] === false) return null;
          return (
            <mesh
              key={m.uuid}
              geometry={m.geometry}
              material={m.material}
              position={m.position}
              quaternion={m.quaternion}
              scale={m.scale}
              castShadow
              receiveShadow
              onUpdate={(self) => {
                const mats = Array.isArray(self.material)
                  ? self.material
                  : [self.material];
                mats.forEach((mat) => {
                  if (!mat) return;
                  mat.clippingPlanes = planes;
                  mat.clipShadows = true;
                  mat.needsUpdate = true;
                });
              }}
            >
              {selected && (
                <Outlines thickness={2.5} color="#c45c3e" screenspace />
              )}
            </mesh>
          );
        })}
      </group>
      {selected &&
        roomLabels.map((room) => (
          <Html
            key={room.name}
            position={room.position}
            center
            distanceFactor={16}
            style={{ pointerEvents: 'none' }}
            zIndexRange={[10, 0]}
          >
            <div className="font-bai text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-900/75 text-white">
              {room.name}
            </div>
          </Html>
        ))}
    </group>
  );

  if (!selected) {
    return (
      <group position={[x, y, z]} rotation-y={rotY}>
        {body}
      </group>
    );
  }

  return (
    <PivotControls
      key={structure.id}
      matrix={matrix}
      autoTransform={false}
      anchor={[0, 0, 0]}
      scale={3}
      lineWidth={2}
      activeAxes={[true, true, true]}
      disableScaling
      annotations={false}
      depthTest={false}
      onDrag={(local) => {
        matrix.copy(local);
        const pos = new THREE.Vector3();
        const quat = new THREE.Quaternion();
        const scl = new THREE.Vector3();
        local.decompose(pos, quat, scl);
        const euler = new THREE.Euler().setFromQuaternion(quat, 'YXZ');
        pending.current = {
          position: [pos.x, pos.y, pos.z],
          rotationY: euler.y,
        };
      }}
      onDragEnd={() => {
        if (!pending.current) return;
        onMove?.(pending.current.position, pending.current.rotationY);
        pending.current = null;
      }}
    >
      {body}
    </PivotControls>
  );
}

/**
 * Map SoftPlan mesh names → Roof / Walls / Interior toggles.
 * OBJ/GLB may keep either viewer prefixes (`layer_roof__…`) or SoftPlan
 * group buckets (`roof`, `floor_0`, `ceiling`) from the `g` line after OBJ→GLB.
 */
function layerKeyFromName(name = '') {
  const n = String(name).toLowerCase();
  if (
    n.startsWith('layer_roof') ||
    n === 'roof' ||
    n.startsWith('roof_')
  ) {
    return 'roof';
  }
  if (
    n.startsWith('layer_interior') ||
    n === 'ceiling' ||
    n.startsWith('ceiling_')
  ) {
    return 'interior';
  }
  // floor_* SoftPlan buckets + explicit walls prefix → walls/structure
  if (
    n.startsWith('layer_walls') ||
    n.startsWith('floor_') ||
    n === 'walls'
  ) {
    return 'walls';
  }
  return 'walls';
}

function prepareLayeredMeshes(scene) {
  const root = scene.clone(true);
  const box = new THREE.Box3().setFromObject(root);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  root.position.x -= center.x;
  root.position.y -= center.y;
  root.position.z -= center.z;
  root.position.y += size.y / 2;
  root.updateMatrixWorld(true);

  const list = [];
  root.traverse((obj) => {
    if (!obj.isMesh) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    mats.forEach((m) => {
      if (!m) return;
      m.side = THREE.DoubleSide;
      m.needsUpdate = true;
    });
    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    obj.matrixWorld.decompose(pos, quat, scl);
    const name = obj.name || obj.parent?.name || '';
    list.push({
      uuid: obj.uuid,
      name,
      geometry: obj.geometry,
      material: obj.material,
      position: pos.toArray(),
      quaternion: [quat.x, quat.y, quat.z, quat.w],
      scale: scl.toArray(),
    });
  });
  return list;
}

// Seed default; SpeckleViewerPage / catalogModelPreload.js eager-loads the rest.
preloadCatalogModel(DEFAULT_SITE_PLANNER_MODEL);

export default SitePlannerViewer;
